import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PERSON_DEI_ANSWER_KEYS,
  type PersonDeiDeleteResponse,
  type PersonDeiInput,
  type PersonDeiResponse,
  type PersonDeiWire,
  type PersonDetailWire,
} from '@releve/shared';
import { authedDelete, authedPut } from '@/lib/api';
import { personKeys } from '@/hooks/usePeople';
import type { PersonCreateDEIData } from '@/types/people';

/**
 * DEI (GDPR Art. 9 special-category data) — ORG ADMINS ONLY, in the API and
 * in RLS. There is no separate DEI read: an admin's `PersonDetailWire.dei`
 * carries the row, and it is `null` for everyone else.
 */

function isDetail(value: unknown): value is PersonDetailWire {
  return typeof value === 'object' && value !== null && 'archive_only_reasons' in value;
}

/**
 * The person's DEI row, read off the detail query (`usePersonByCode`).
 *
 * Accepts the detail itself (preferred) or, for the legacy form, a person id —
 * resolved against the cached detail queries under `['people']`. The edit
 * host only mounts the form once the detail has resolved, so the cache holds
 * it. `null` for a new person, a non-admin, or a person with no DEI row.
 * Never fetches, so `isLoading` is always `false`.
 */
export function usePersonDEI(person?: PersonDetailWire | string | null): {
  data: PersonDeiWire | null;
  isLoading: false;
} {
  const queryClient = useQueryClient();
  if (!person) return { data: null, isLoading: false };
  if (typeof person !== 'string') return { data: person.dei ?? null, isLoading: false };

  const cached = queryClient
    .getQueriesData<PersonDetailWire | null>({ queryKey: personKeys.all })
    .find(([key, data]) => key[2] === 'detail' && isDetail(data) && data.id === person)?.[1];
  return { data: cached?.dei ?? null, isLoading: false };
}

/**
 * The PUT body: the FULL answer set (PUT replaces the row — an absent answer
 * is written as NULL) plus consent. `person_id` / `organization_id` travel in
 * the path, never the body. Empty strings and empty arrays clear an answer.
 */
function toDeiBody(data: PersonCreateDEIData): PersonDeiInput {
  const body: Record<string, unknown> = { dei_data_consent: data.dei_data_consent };
  for (const key of PERSON_DEI_ANSWER_KEYS) {
    const value: unknown = data[key];
    body[key] = value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
      ? null
      : value;
  }
  if (data.dei_survey_completed_at !== undefined) {
    body.dei_survey_completed_at = data.dei_survey_completed_at;
  }
  return body as PersonDeiInput;
}

/**
 * Create or replace a person's DEI row (`PUT …/people/:personId/dei`, admin
 * only — a non-admin gets 403). Call-compatible with the old upsert:
 * `{ person_id, organization_id, …answers, dei_data_consent }`. Withdrawing
 * consent is `dei_data_consent: false` with every answer empty.
 */
export function useUpsertPersonDEI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PersonCreateDEIData): Promise<PersonDeiWire> => {
      const result = await authedPut<PersonDeiResponse>(
        `/api/v1/people/orgs/${data.organization_id}/people/${data.person_id}/dei`,
        toDeiBody(data),
      );
      return result.dei;
    },
    onSuccess: (dei) => {
      void queryClient.invalidateQueries({ queryKey: personKeys.org(dei.organization_id) });
    },
  });
}

/** Delete a person's DEI row (admin only). Resolves to `false` when there was none. */
export function useDeletePersonDEI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      personId,
    }: {
      organizationId: string;
      personId: string;
    }): Promise<boolean> => {
      const result = await authedDelete<PersonDeiDeleteResponse>(
        `/api/v1/people/orgs/${organizationId}/people/${personId}/dei`,
      );
      return result.deleted;
    },
    onSuccess: (_deleted, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: personKeys.org(organizationId) });
    },
  });
}
